import { useForm } from "react-hook-form";

import type { BuildingDto } from "@/api/types";
import { Breadcrumbs } from "@/components/Breadcrumbs/Breadcrumbs";
import { Form } from "@/components/Form/Form";
import { FormCheckbox } from "@/components/Form/FormCheckbox";
import { FormInput } from "@/components/Form/FormInput";
import { DocumentPage } from "@/components/Page/DocumentPage";
import { Button } from "@/design-system/Button";
import { FormField } from "@/design-system/FormField";
import { canCreateInterestGroup } from "@/helpers/permissions";
import { createRequiredStringRule } from "@/helpers/rules";
import { usePermission } from "@/hooks/usePermission";
import { useSlug } from "@/hooks/useSlug";
import { routes } from "@/routes";
import { useTranslation } from "@/translations";

export type BuildingFormValues = {
  name: string;
  shouldCreateRealEstateGroup?: boolean;
};

export interface BuildingFormProps {
  existingBuildings: BuildingDto[];
  isEditMode: boolean;
  defaultValues?: Partial<BuildingFormValues>;
  isSubmitting: boolean;
  onSubmit: (data: BuildingFormValues) => void;
}

export function BuildingForm({
  existingBuildings,
  isEditMode,
  defaultValues,
  isSubmitting,
  onSubmit,
}: BuildingFormProps): React.ReactNode {
  const formMethods = useForm<BuildingFormValues>({ defaultValues });
  const { t } = useTranslation();
  const slug = useSlug();
  const hasPermission = usePermission();

  const title = isEditMode ? t("page.buildings.edit.title") : t("page.buildings.new.title");
  const existingBuildingNames = new Set(existingBuildings.map((x) => x.name.toString().trim().toLowerCase()));

  return (
    <DocumentPage
      contentSize="constrained"
      tabTitle={title}
      breadcrumbs={
        <Breadcrumbs
          pages={[
            {
              name: t("page.buildings.title"),
              to: routes.buildings.list({
                slug,
              }),
            },
            {
              name: title,
            },
          ]}
        />
      }
    >
      <Form.Root formMethods={formMethods} onSubmit={onSubmit}>
        <Form.Container>
          <Form.Body>
            <FormField label={t("page.buildings.form.name")} required>
              <FormInput<BuildingFormValues, "name">
                name="name"
                rules={{
                  validate: {
                    required: createRequiredStringRule(t, "page.buildings.form.name"),
                    isDuplicate(value: string) {
                      if (existingBuildingNames.has(value.trim().toLowerCase())) {
                        return t("page.buildings.form.name.error.duplicate");
                      }
                    },
                  },
                }}
              />
            </FormField>
            {!isEditMode && hasPermission(canCreateInterestGroup) && (
              <FormCheckbox
                name="shouldCreateRealEstateGroup"
                label={t("page.buildings.form.should-create-real-estate-group")}
              />
            )}
          </Form.Body>
          <Form.Footer
            renderPrimaryAction={() => (
              <Button type="submit" isLoading={isSubmitting}>
                {isEditMode ? t("page.buildings.edit.submit") : t("page.buildings.new.submit")}
              </Button>
            )}
          />
        </Form.Container>
      </Form.Root>
    </DocumentPage>
  );
}
